import React, { useEffect, useState } from 'react';

/**
 * Sélecteur de zone de stockage en deux étapes : colonne (lettre A-Z) puis
 * niveau (chiffre 0-9), pour éviter une liste déroulante de 260+ entrées.
 * Suppose des codes de la forme "ZONE-LETTRECHIFFRE" (ex: "AT-D2").
 * Si un jour d'autres zones que "AT" sont réintroduites, un sélecteur de
 * zone apparaît automatiquement en plus.
 */
const CODE_REGEX = /^([A-Z]+)-([A-Z])(\d)$/;

export function StockageSelect({
  id,
  label,
  value,
  setter,
  options = [],
  required = false,
  readonly = false,
}) {
  const parsed = options
    .filter((o) => o.value !== '')
    .map((o) => {
      const match = CODE_REGEX.exec(o.label);
      return match
        ? {
            value: o.value,
            label: o.label,
            zone: match[1],
            colonne: match[2],
            niveau: match[3],
          }
        : null;
    })
    .filter(Boolean);

  const zones = [...new Set(parsed.map((p) => p.zone))];
  const [selectedZone, setSelectedZone] = useState(zones[0] || '');
  const [selectedColonne, setSelectedColonne] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('');

  // Initialise les sélecteurs à partir de la valeur actuelle de la fiche
  useEffect(() => {
    const current = parsed.find((p) => p.value === value);
    if (current) {
      setSelectedZone(current.zone);
      setSelectedColonne(current.colonne);
      setSelectedNiveau(current.niveau);
    }
    // eslint-disable-next-line
  }, [value]);

  const colonnesDisponibles = [
    ...new Set(
      parsed.filter((p) => p.zone === selectedZone).map((p) => p.colonne)
    ),
  ].sort();

  const niveauxDisponibles = [
    ...new Set(
      parsed
        .filter((p) => p.zone === selectedZone && p.colonne === selectedColonne)
        .map((p) => p.niveau)
    ),
  ].sort();

  const applySelection = (zone, colonne, niveau) => {
    if (!zone || !colonne || !niveau) return;
    const match = parsed.find(
      (p) => p.zone === zone && p.colonne === colonne && p.niveau === niveau
    );
    if (match) setter(match.value);
  };

  if (readonly) {
    const current = parsed.find((p) => p.value === value);
    return (
      <div className="flex flex-col text-left px-2 mb-4">
        <label htmlFor={id}>{label}</label>
        <div className="w-full border-2 border-stbSecond rounded pl-2 py-1 my-1 bg-gray-400 text-black cursor-not-allowed">
          {current?.label || 'Non renseignée'}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col text-left px-2 mb-4">
      <label htmlFor={id}>
        {label}
        {required ? <span className="text-red-600"> *</span> : ''}
      </label>
      <div className="flex gap-2">
        {zones.length > 1 && (
          <select
            className="border-2 border-stbSecond rounded pl-2 py-1 my-1 text-black bg-white"
            value={selectedZone}
            onChange={(e) => {
              setSelectedZone(e.target.value);
              setSelectedColonne('');
              setSelectedNiveau('');
            }}
          >
            {zones.map((z) => (
              <option value={z} key={z}>
                {z}
              </option>
            ))}
          </select>
        )}
        <select
          id={id}
          className="border-2 border-stbSecond rounded pl-2 py-1 my-1 text-black bg-white flex-1"
          value={selectedColonne}
          onChange={(e) => {
            const colonne = e.target.value;
            setSelectedColonne(colonne);
            applySelection(selectedZone, colonne, selectedNiveau);
          }}
        >
          <option value="">Colonne...</option>
          {colonnesDisponibles.map((c) => (
            <option value={c} key={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          className="border-2 border-stbSecond rounded pl-2 py-1 my-1 text-black bg-white flex-1"
          value={selectedNiveau}
          onChange={(e) => {
            const niveau = e.target.value;
            setSelectedNiveau(niveau);
            applySelection(selectedZone, selectedColonne, niveau);
          }}
        >
          <option value="">Niveau...</option>
          {niveauxDisponibles.map((n) => (
            <option value={n} key={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      {selectedColonne && selectedNiveau && (
        <span className="text-xs italic mt-1">
          Zone sélectionnée : {selectedZone}-{selectedColonne}
          {selectedNiveau}
        </span>
      )}
    </div>
  );
}
